import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { articleHashFor, mandateHashFor } from '@/hooks/useHashRoute';
import { fmtText } from '@/utils/fmtText';
import type { Instrument } from '@/types';
import styles from './ArticleReaderPage.module.css';
import { PrintLink } from './PrintLink';
import { TourLink } from './TourLink';
import { GuidedTour, buildArticleReaderSteps } from './WelcomeTour';

// Instrument articles (e.g. gl_amlr_10_4 §1.2) may carry term→text reference
// tables (Legislative references / Abbreviations) ahead of the numbered body.
// Render them the same way MandateViewerPage does so the standalone reader used by
// the 2-col mandate view shows the full structure. Inert for the four main acts,
// which carry no refTables.
function RefTables({ tables, color }: { tables: any[]; color: string }) {
  if (!tables || tables.length === 0) return null;
  return (
    <>
      {tables.map((rt: any, ri: number) => (
        <div className={styles.refTable} key={`rt-${ri}`}>
          <div className={styles.paraHeading} style={{ color }}>{rt.heading}</div>
          <dl className={styles.refDl}>
            {(rt.entries || []).map((e: any, ei: number) => (
              <div className={styles.refRow} key={`rt-${ri}-${ei}`}>
                <dt className={styles.refTerm} style={{ color }}>{e.term}</dt>
                <dd className={styles.refDef}>
                  {e.text}
                  {(e.ojRef || e.eli) && (
                    <span className={styles.refSrc}>
                      {e.ojRef && <span>{e.ojRef}</span>}
                      {e.eli && <a href={e.eli} target="_blank" rel="noopener" style={{ color }}>ELI&nbsp;↗</a>}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </>
  );
}
import amldData from '@/data/acts/amld.json';
import amlrData from '@/data/acts/amlr.json';
import amlarData from '@/data/acts/amlar.json';
import tfrData from '@/data/acts/tfr.json';
import instrumentsData from '@/data/instruments/index.json';
import citedNormsData from '@/data/cited_norms.json';

const ALL_ACTS: Record<string, any> = { amld: amldData, amlr: amlrData, amlar: amlarData, tfr: tfrData };
const INSTRUMENTS = instrumentsData as Record<string, Instrument>;
type CitedNorm = { shortName: string; fullName: string; code: string; url: string };
const CITED_NORMS = citedNormsData as Record<string, CitedNorm>;
const ARTICLE_TOUR_KEY = 'aml_tour_article_dismissed';
const ACT_COLORS: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };
// Map a base-act id to its cited-norms registry key (used to build the
// "Referenced legislation" box for mandate articles from their cross-references).
const ACT_NORM_KEY: Record<string, string> = { amld: 'dir_eu_2024_1640', amlr: 'reg_eu_2024_1624', amlar: 'reg_eu_2024_1620', tfr: 'reg_eu_2023_1113' };
const STATUS_COLORS: Record<string, string> = {
  adopted: '#6D28D9', final_report: '#15803D', consultation_closed: '#1D4ED8',
  consultation_open: '#B45309', upcoming: '#0369A1', planned: '#475569',
};

// EUR-Lex ELI -> anchor #art_N (without regex/backslash)
function toAnchorEli(u: string): string {
  if (!u) return '';
  const parts = u.split('/');
  const i = parts.findIndex(p => p.indexOf('art_') === 0);
  if (i < 0) return u;
  const art = parts[i];
  parts.splice(i, 1);
  return parts.join('/') + '#' + art;
}

function findArticle(actId: string, num: string): { article: any; footnotes: any[] } | null {
  const act = ALL_ACTS[actId];
  if (!act) return null;
  const wanted = String(num);
  for (const ch of (act.chapters || [])) {
    for (const a of (ch.articles || [])) if (String(a.num) === wanted) return { article: a, footnotes: act.footnotes || [] };
    for (const sec of (ch.sections || [])) {
      for (const a of (sec.articles || [])) if (String(a.num) === wanted) return { article: a, footnotes: act.footnotes || [] };
    }
  }
  return null;
}

const CELEX_TO_ACT: Record<string, string> = { '2024/1624': 'amlr', '2024/1640': 'amld', '2024/1620': 'amlar', '2023/1113': 'tfr' };

// Mandate articles' structured crossRefs are often empty, and self-references
// ("Article N of this Regulation") cannot be expressed in the act-based crossRef
// schema. So for mandate articles we derive references from the verbatim text:
//  - external: "Article(s) N … of Regulation/Directive (EU) YYYY/NNNN" -> base-act article
//  - self:     "Article(s) N … of this Regulation"                     -> the mandate's own article
function parseMandateRefs(text: string): { external: { act: string; num: number }[]; self: number[] } {
  const external: { act: string; num: number }[] = [];
  const selfSet = new Set<number>();
  const re = /Articles?\s+([^.;:]+?)\s+of\s+(this Regulation|(?:Regulation|Directive) \(EU\) (\d{4}\/\d{4}))/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const nums = Array.from(('Article ' + m[1]).matchAll(/Article\s+(\d+)/g)).map(x => parseInt(x[1], 10));
    if (m[2].indexOf('this Regulation') === 0) {
      nums.forEach(n => selfSet.add(n));
    } else {
      const act = CELEX_TO_ACT[m[3]];
      if (act) nums.forEach(n => external.push({ act, num: n }));
    }
  }
  const seen = new Set<string>();
  const ext = external.filter(e => { const k = e.act + ':' + e.num; if (seen.has(k)) return false; seen.add(k); return true; });
  return { external: ext, self: Array.from(selfSet).sort((a, b) => a - b) };
}

type Props = { actId: string; num: string; mandateId?: string };

export function ArticleReaderPage({ actId, num, mandateId }: Props) {
  const found = useMemo(() => {
    if (mandateId) {
      const inst = INSTRUMENTS[mandateId] as any;
      // Guideline-column articles are addressed as "<colIndex>~<num>" to disambiguate
      // the two guidelines of a two-column mandate (which share section numbers like 4.1).
      const gm = String(num).match(/^(\d+)~(.+)$/);
      if (gm && inst?.guidelineColumns?.[Number(gm[1])]) {
        const realNum = gm[2];
        const ga = inst.guidelineColumns[Number(gm[1])].articles?.find((x: any) => String(x.num) === realNum);
        return ga ? { article: ga, footnotes: [] as any[] } : null;
      }
      const a = inst?.articles?.find((x: any) => String(x.num) === String(num));
      return a ? { article: a, footnotes: [] as any[] } : null;
    }
    return findArticle(actId, num);
  }, [actId, num, mandateId]);
  const mandate = mandateId ? (INSTRUMENTS[mandateId] as any) : null;
  const color = ACT_COLORS[actId] || '#333';
  // Side columns: opened only from "Referenced articles" box; depth 1 (no nesting).
  const [sideArts, setSideArts] = useState<Array<{ actId: string; num: string }>>([]);
  // Reader tour: first visit (own dismissal key), re-openable from the Tour link.
  const [showTour, setShowTour] = useState(false); // secondary tours never auto-open — only via the Tour link (Erik, 7 Oct 2026)
  const addSide = (sActId: string, sNum: string | number) => {
    setSideArts(prev => prev.some(s => s.actId === sActId && s.num === String(sNum)) ? prev : [...prev, { actId: sActId, num: String(sNum) }]);
  };
  const removeSide = (sActId: string, sNum: string) => {
    setSideArts(prev => prev.filter(s => !(s.actId === sActId && s.num === sNum)));
  };

  // Document title for the tab
  useEffect(() => {
    if (found) document.title = `Art. ${found.article.num} — ${found.article.title}`;
  }, [found]);

  // Align all column titles to the same height (max), so the colored underline
  // is visually aligned across the main article and side columns regardless of
  // how many lines each title takes.
  const articleAreaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const align = () => {
      const root = articleAreaRef.current;
      if (!root) return;
      const titles = root.querySelectorAll<HTMLElement>(`.${styles.title}`);
      let max = 0;
      titles.forEach(t => { t.style.minHeight = '0px'; });
      titles.forEach(t => { if (t.offsetHeight > max) max = t.offsetHeight; });
      titles.forEach(t => { t.style.minHeight = max + 'px'; });
    };
    // Use a microtask + animation frame to ensure layout settled (fonts, images).
    const id = requestAnimationFrame(align);
    window.addEventListener('resize', align);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', align);
    };
  }, [sideArts, found]);

  // Horizontal scroll indicators (› / ‹) — position: fixed, anchored exactly
  // at the boundary between articleArea and boxwrap (right side) and at the
  // left edge of the viewport (left side). Visible only when overflow exists.
  const boxwrapRef = useRef<HTMLDivElement>(null);
  const [boxwrapW, setBoxwrapW] = useState(0);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);
  useEffect(() => {
    const el = articleAreaRef.current;
    if (!el) return;
    const update = () => {
      setHasOverflowLeft(el.scrollLeft > 4);
      setHasOverflowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
      const bw = boxwrapRef.current;
      if (bw) setBoxwrapW(bw.offsetWidth);
    };
    const rafId = requestAnimationFrame(update);
    const tId = window.setTimeout(update, 80);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (boxwrapRef.current) ro.observe(boxwrapRef.current);
    el.querySelectorAll<HTMLElement>(`.${styles.acol}`).forEach(c => ro.observe(c));
    window.addEventListener('resize', update);
    el.addEventListener('scroll', update, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(tId);
      ro.disconnect();
      window.removeEventListener('resize', update);
      el.removeEventListener('scroll', update);
    };
  }, [sideArts, found]);
  const scrollByCol = (dir: 1 | -1) => {
    articleAreaRef.current?.scrollBy({ left: dir * 720, behavior: 'smooth' });
  };

  if (!found) {
    return (
      <div style={{ maxWidth: 720, margin: '40px auto', padding: '0 24px', fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ color: '#ef4444' }}>Article not found</h1>
        <p>No article matches <code>{actId} / {num}</code>.</p>
        <p><a href="#/">← Back to home</a></p>
      </div>
    );
  }

  const { article, footnotes } = found;
  const paras = article.textEN?.paragraphs || [];
  const notes = article.textEN?.notes || [];
  const fnRefs: number[] = article.textEN?.footnoteRefs || [];
  const verified = article.textEN?.verified === true;
  const eliEn = toAnchorEli(article.textEN?.source || '');
  const eliIt = eliEn.replace('/oj/eng', '/oj/ita');
  const crossRefs: any[] = article.crossRefs || [];
  const linkedMandates: string[] = article.rtsIts || [];

  // Mandate mode: derive references from the verbatim text (structured crossRefs are
  // frequently empty for mandate articles, and self-references can't be expressed in
  // the act-based crossRef schema).
  const mandateDerived = useMemo(
    () => (mandateId ? parseMandateRefs((article.textEN?.paragraphs || []).map((p: any) => p.text).join(' ')) : null),
    [mandateId, article]
  );

  const citedKeys: string[] = mandateId
    ? Array.from(new Set([
        ...crossRefs.map((r: any) => ACT_NORM_KEY[r.act]),
        ...((mandateDerived?.external) || []).map(e => ACT_NORM_KEY[e.act]),
      ].filter(Boolean)))
    : ((article as any).citedNorms || []);
  // Box "Referenced legislation" is DYNAMIC: it shows the deduplicated union of
  // norms cited by the main article + by all open side columns. (Box "Referenced
  // articles" stays static — it's the navigation hub of the main article.)
  const allCitedKeys = useMemo(() => {
    const keys = new Set<string>(citedKeys);
    for (const s of sideArts) {
      const f = findArticle(s.actId, s.num);
      ((f?.article as any)?.citedNorms || []).forEach((k: string) => keys.add(k));
    }
    return Array.from(keys);
  }, [citedKeys, sideArts]);
  const cited = allCitedKeys.map(k => CITED_NORMS[k]).filter(Boolean) as CitedNorm[];

  // Cross-ref article lookups (for inline badges + Box 1 titles).
  let refsResolved: any[];
  if (mandateId && mandateDerived) {
    const ext = [
      ...crossRefs.map((r: any) => ({ act: r.act, num: parseInt(String(r.target).replace('art_', ''), 10), type: r.type || 'see-also' })),
      ...mandateDerived.external.map(e => ({ act: e.act, num: e.num, type: 'see-also' })),
    ];
    const seen = new Set<string>();
    refsResolved = ext.filter(e => { const k = e.act + ':' + e.num; if (!e.num || seen.has(k)) return false; seen.add(k); return true; })
      .map(e => ({ act: e.act, refNum: e.num, type: e.type, refTitle: findArticle(e.act, String(e.num))?.article?.title || null, self: false }))
      .filter(r => r.refTitle !== null);
    const inst = INSTRUMENTS[mandateId] as any;
    for (const n of mandateDerived.self) {
      const a = inst?.articles?.find((x: any) => String(x.num) === String(n));
      if (a) refsResolved.push({ act: actId, refNum: n, type: 'see-also', refTitle: a.title, self: true });
    }
  } else {
    refsResolved = crossRefs.map(r => {
      const refNum = parseInt(String(r.target).replace('art_', ''), 10);
      const refFound = findArticle(r.act, String(refNum));
      return { ...r, refNum, refTitle: refFound?.article?.title || null, self: false };
    }).filter(r => r.refTitle !== null);
  }

  return (
    <div className={`${styles.page} ${sideArts.length ? styles.pageSide : ''}`}>
      <GuidedTour open={showTour} onClose={() => setShowTour(false)} steps={buildArticleReaderSteps()} storageKey={ARTICLE_TOUR_KEY} />
      {sideArts.length > 0 && (
        <nav className={styles.openNav} aria-label="Open articles">
          <span className={styles.openNavLabel}>Open ({sideArts.length + 1})</span>
          <button className={styles.openNavChip} style={{ borderColor: color, color }}
            onClick={() => document.getElementById(`col-${actId}-${num}`)?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })}>
            {actId.toUpperCase()} Art. {num}
          </button>
          {sideArts.map(s => {
            const c = ACT_COLORS[s.actId] || '#333';
            return (
              <span key={`${s.actId}:${s.num}`} className={styles.openNavChip} style={{ borderColor: c, color: c }}>
                <button className={styles.openNavGo} onClick={() => document.getElementById(`col-${s.actId}-${s.num}`)?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })}>
                  {s.actId.toUpperCase()} Art. {s.num}
                </button>
                <button className={styles.openNavClose} onClick={() => removeSide(s.actId, s.num)} title="Close" aria-label={`Close ${s.actId.toUpperCase()} Art. ${s.num}`}>×</button>
              </span>
            );
          })}
        </nav>
      )}
      <div className={styles.articleArea} ref={articleAreaRef}>
        <div className={styles.acol} style={{ ['--ac' as any]: color }} id={`col-${actId}-${num}`}>
          {mandateId && mandate && (
            <div style={{ marginBottom: 10 }}>
              <a href={mandateHashFor(mandateId)} style={{ color, fontWeight: 700, fontSize: 12.5, textDecoration: 'none' }}>
                ← {mandate.type} · {mandate.legalBasis}
              </a>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', lineHeight: 1.4, marginTop: 4 }}>
                {mandate.cpTitle || mandate.title}
              </div>
            </div>
          )}
          <h1 className={styles.title} style={{ color, borderBottomColor: color }} data-tour="ar-title">
            Article {article.num} — {article.title}
          </h1>

          <div className={styles.metaRow} data-tour="ar-meta">
            {mandateId && mandate ? (
              <div className={styles.sources}>
                Official source — AMLA <a href={mandate.documentUrl} target="_blank" rel="noopener">document ↗</a>{' '}
                <span style={{ color: '#9aa3ab' }}>· EUR-Lex EN/IT once adopted</span>
              </div>
            ) : eliEn ? (
              <div className={styles.sources}>
                <strong>Official sources — EUR-Lex</strong>{' '}
                <a href={eliEn} target="_blank" rel="noopener">EN ↗</a> &nbsp;
                <a href={eliIt} target="_blank" rel="noopener">IT ↗</a>
              </div>
            ) : <span />}
            <span className={styles.metaTools}>
              <TourLink accent={color} onClick={() => setShowTour(true)} />
              <PrintLink accent={color} />
            </span>
          </div>

          {article.summary && (
            <details className={styles.summary} style={{ borderLeftColor: color }} data-tour="ar-summary">
              <summary><strong>Summary</strong> <span className={styles.summaryHint}>AI-generated · click to show</span></summary>
              <p>{article.summary}</p>
              {article.summary_disclaimer && (
                <div className={styles.disclaimer}>{article.summary_disclaimer}</div>
              )}
            </details>
          )}

          {refsResolved.length > 0 && (
            <div data-tour="ar-xrefs">
              <div className={styles.sectionLabel}>
                Cross-references<span className={styles.rhint}>each opens in a new tab ↗</span>
              </div>
              <div className={styles.refs}>
                {refsResolved.map((r, i) => {
                  const c = r.self ? color : (ACT_COLORS[r.act] || '#333');
                  return (
                    <a
                      key={i}
                      className={styles.refBadge}
                      href={r.self && mandateId ? mandateHashFor(mandateId, r.refNum) : articleHashFor(r.act, r.refNum)}
                      target="_blank"
                      rel="noopener"
                      style={{ borderColor: c, color: c, background: c + '21' }}
                    >
                      {r.self ? `Art.${r.refNum}` : `${r.act.toUpperCase()} Art.${r.refNum}`} <em>{r.type}</em>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {linkedMandates.length > 0 && (
            <div data-tour="ar-mandates">
              <div className={styles.sectionLabel}>Linked RTS / ITS / GL</div>
              <div className={styles.mandates}>
                {linkedMandates.map(id => {
                  const inst = INSTRUMENTS[id];
                  if (!inst) return null;
                  const c = STATUS_COLORS[inst.status] || '#475569';
                  const url = inst.documentUrl || inst.consultationUrl || 'https://www.amla.europa.eu/policy/regulatory-instruments_en';
                  return (
                    <a key={id} className={styles.mandateItem} href={url} target="_blank" rel="noopener" style={{ borderLeftColor: c }}>
                      <div className={styles.mandateRow}>
                        <span className={styles.mandateType}>{inst.type}</span>
                        <span className={styles.mandateStatus} style={{ color: c }}>{inst.statusLabel}</span>
                        <span className={styles.mandateArrow} style={{ color: c }}>↗</span>
                      </div>
                      <div className={styles.mandateBasis}>{inst.legalBasis}</div>
                      <div className={styles.mandateTitle}>{inst.title}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {verified ? (
            <div className={styles.bannerOk} data-tour="ar-banner">
              ✓ Full text taken verbatim from the{' '}
              <a href={eliEn} target="_blank" rel="noopener">official EUR-Lex source ↗</a>
            </div>
          ) : (
            <div className={styles.bannerWarn} data-tour="ar-banner">⚠ Unverified text — always check the official source</div>
          )}

          <div className={styles.sectionLabel} data-tour="ar-text">Full text</div>
          <RefTables tables={article.textEN?.refTables || []} color={color} />
          {paras.map((p: any, i: number) => (
            <Fragment key={i}>
              {p.heading && <div className={styles.paraHeading} style={{ color }}>{p.heading}</div>}
              <div className={styles.para}>
                <span className={styles.paraNum} style={{ color }}>{p.num}.</span>
                <div className={styles.paraText} dangerouslySetInnerHTML={{ __html: fmtText(p.text) }} />
              </div>
            </Fragment>
          ))}

          {notes.length > 0 && (
            <>
              <div className={styles.sectionLabel}>Notes</div>
              {notes.map((n: string, i: number) => <p key={i}>{n}</p>)}
            </>
          )}

          {fnRefs.length > 0 && footnotes.length > 0 && (
            <div className={styles.footnotes}>
              <div className={styles.sectionLabel}>Footnotes</div>
              {fnRefs.map(n => {
                const f = footnotes.find((x: any) => x.num === n);
                if (!f) return null;
                return (
                  <div key={n} className={styles.fnItem}>
                    <span className={styles.fnNum}>({n})</span>
                    <span dangerouslySetInnerHTML={{ __html: f.text }} />
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.footer}>
            Always verify against the official EUR-Lex source before relying on this text.
          </div>
        </div>

        {sideArts.map((s) => (
          <SideColumn key={`${s.actId}:${s.num}`} actId={s.actId} num={s.num} onClose={() => removeSide(s.actId, s.num)} />
        ))}
      </div>

      {(refsResolved.length > 0 || cited.length > 0) && (
        <div className={styles.boxwrap} ref={boxwrapRef}>
          {refsResolved.length > 0 && (
            <div className={styles.box} data-tour="ar-refbox">
              <div className={styles.boxH} style={{ borderColor: color, color }}>
                📑 Referenced articles
                <span className={styles.bhint}>click → side by side ⇆</span>
              </div>
              <div className={styles.boxB}>
                {refsResolved.map((r, i) => {
                  const c = r.self ? color : (ACT_COLORS[r.act] || '#333');
                  if (r.self && mandateId) {
                    return (
                      <a key={i} className={styles.cnItem} href={mandateHashFor(mandateId, r.refNum)} target="_blank" rel="noopener">
                        <span className={styles.cnShort} style={{ color: c }}>Art.{r.refNum} <em>{r.type}</em></span>
                        {r.refTitle && <span className={styles.cnCode}>{r.refTitle}</span>}
                      </a>
                    );
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      className={styles.cnItem}
                      onClick={() => addSide(r.act, r.refNum)}
                    >
                      <span className={styles.cnShort} style={{ color: c }}>
                        {r.act.toUpperCase()} Art.{r.refNum} <em>{r.type}</em>
                      </span>
                      {r.refTitle && <span className={styles.cnCode}>{r.refTitle}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {cited.length > 0 && (
            <div className={styles.box} data-tour="ar-citedbox">
              <div className={styles.boxH} style={{ borderColor: color, color }}>
                ⚖️ Referenced legislation
                <span className={styles.bhint}>click → EUR-Lex in a new tab ↗</span>
              </div>
              <div className={styles.boxB}>
                {cited.map((n, i) => (
                  <a key={i} className={styles.cnItem} href={n.url} target="_blank" rel="noopener" title={n.fullName}>
                    <span className={styles.cnShort}>{n.shortName}</span>
                    <span className={styles.cnCode}>{n.code}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scroll indicators — position:fixed, never overlap columns */}
      <button
        type="button"
        className={`${styles.scrollArrow} ${styles.scrollArrowL}`}
        onClick={() => scrollByCol(-1)}
        title="Scroll left"
        aria-label="Scroll left"
        style={{ visibility: hasOverflowLeft ? 'visible' : 'hidden', pointerEvents: hasOverflowLeft ? 'auto' : 'none' }}
      >‹</button>
      <button
        type="button"
        className={styles.scrollArrow}
        onClick={() => scrollByCol(1)}
        title="Scroll right"
        aria-label="Scroll right"
        style={{
          visibility: hasOverflowRight ? 'visible' : 'hidden',
          pointerEvents: hasOverflowRight ? 'auto' : 'none',
          right: (boxwrapW + 6) + 'px',
        }}
      >›</button>
    </div>
  );
}

/**
 * SideColumn — compact view of a referenced article shown side-by-side.
 * Contains: title, EUR-Lex sources, summary (with disclaimer), full text, footnotes.
 * No box, no cross-refs, no mandates, no verbatim banner. Depth-1 only.
 */
function SideColumn({ actId, num, onClose }: { actId: string; num: string; onClose: () => void }) {
  const found = useMemo(() => findArticle(actId, num), [actId, num]);
  const color = ACT_COLORS[actId] || '#333';
  if (!found) {
    return (
      <div className={styles.acol}>
        <h1 className={styles.title} style={{ color: '#ef4444', borderBottomColor: '#ef4444' }}>
          <button className={styles.colClose} onClick={onClose} title="Close" aria-label="Close">×</button>
          Article not found
        </h1>
        <p>No article matches <code>{actId} / {num}</code>.</p>
      </div>
    );
  }
  const { article, footnotes } = found;
  const paras = article.textEN?.paragraphs || [];
  const fnRefs: number[] = article.textEN?.footnoteRefs || [];
  const eliEn = toAnchorEli(article.textEN?.source || '');
  const eliIt = eliEn.replace('/oj/eng', '/oj/ita');
  return (
    <div className={styles.acol} style={{ ['--ac' as any]: color }} id={`col-${actId}-${num}`}>
      <h1 className={styles.title} style={{ color, borderBottomColor: color }}>
        <button className={styles.colClose} onClick={onClose} title="Close" aria-label="Close">×</button>
        Article {article.num} — {article.title}
      </h1>
      {eliEn && (
        <div className={styles.sources}>
          <strong>Official sources — EUR-Lex</strong>{' '}
          <a href={eliEn} target="_blank" rel="noopener">EN ↗</a> &nbsp;
          <a href={eliIt} target="_blank" rel="noopener">IT ↗</a>
        </div>
      )}
      {article.summary && (
        <div className={styles.summary} style={{ borderLeftColor: color }}>
          <strong>Summary</strong>
          <p>{article.summary}</p>
          {article.summary_disclaimer && (
            <div className={styles.disclaimer}>{article.summary_disclaimer}</div>
          )}
        </div>
      )}
      <div className={styles.sectionLabel}>Full text</div>
      <RefTables tables={article.textEN?.refTables || []} color={color} />
      {paras.map((p: any, i: number) => (
        <Fragment key={i}>
          {p.heading && <div className={styles.paraHeading} style={{ color }}>{p.heading}</div>}
          <div className={styles.para}>
            <span className={styles.paraNum} style={{ color }}>{p.num}.</span>
            <div className={styles.paraText} dangerouslySetInnerHTML={{ __html: fmtText(p.text) }} />
          </div>
        </Fragment>
      ))}
      {fnRefs.length > 0 && footnotes.length > 0 && (
        <div className={styles.footnotes}>
          <div className={styles.sectionLabel}>Footnotes</div>
          {fnRefs.map(n => {
            const f = footnotes.find((x: any) => x.num === n);
            if (!f) return null;
            return (
              <div key={n} className={styles.fnItem}>
                <span className={styles.fnNum}>({n})</span>
                <span dangerouslySetInnerHTML={{ __html: f.text }} />
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.footer}>
        Always verify against the official EUR-Lex source before relying on this text.
      </div>
    </div>
  );
}
